/**
 * PATH: packages/email-hub/src/feature/pages/ComposePage.tsx
 * FILE_ID: EMAILHUB-FEATURE-PAGES-COMPOSEPAGE-001
 * PURPOSE: Canonical body of this email pages module. Lifted
 *          from apps/<tenant>/src/features/email/pages/ComposePage.tsx
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/pages/ComposePage.tsx: thin re-export
 *     shim that points back here.
 */

import {
  AlertCircle,
  BarChart3,
  BellRing,
  Briefcase,
  CheckCircle,
  ChevronDown,
  Clock,
  Copy,
  FileSpreadsheet,
  Info,
  Loader2,
  Paperclip,
  Pin,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react'
import RecipientAutocomplete from '../components/RecipientAutocomplete'
import ComposeAttachmentStrip from '../components/ComposeAttachmentStrip'
import SignatureEditorModal from '../components/SignatureEditorModal'
import { isSignedAttachmentName } from '../lib/signedAttachment'
// 2026-05-13: dropped the duplicate inline <OutlookBanner> here.
// EmailLayout already renders <EmailConnectionBanner> above the
// <Outlet> for the same disconnected-Outlook state, and the inline
// version was using the old dark-mode-only colour tokens — the
// stacked-twice + invisible-text banner the operator screenshotted.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import RichTextEditor, { type RichTextEditorHandle } from '../components/RichTextEditor'
import AiComposeButton from '../components/AiComposeButton'
import { EmailVisibilityToggle } from '../components/EmailVisibilityToggle'
import { ComposeInboxRail } from '../components/ComposeInboxRail'
import EmailReader from '../components/EmailReader'
import { useEmailAllocationMembers } from '../hooks/useEmailMembers'
import { SendHoldBanner } from '../components/SendHoldBanner'
import { aiBodyToHtml, transformSelection, runAiInstruction } from '../lib/aiCompose'
import { useUndoSend } from '../compose/useUndoSend'
import { syncUndoSendFromServer } from '../compose/undoSendSync'
import {
  getPinnedFromAccountId,
  setPinnedFromAccount,
  syncFromDefaultsFromServer,
} from '../compose/fromDefaultSync'
import { getUndoSendDelayMs } from '@quotech/ui'
import { useComposePage } from './useComposePage'
import { useComposePreviewEmail } from './composePreviewState'
import { shouldWarnMissingAttachment } from '../lib/attachmentMention'
import { useComposeDragDrop } from './useComposeDragDrop'
import { useToast } from '../../_internal/components/Toast'
import { ComposeWorkbookModal } from '../components/workbook/ComposeWorkbookModal'
import { ComposeChartModal } from '../components/charts/ComposeChartModal'
import {
  followupPresets,
  formatResurfaceTime,
  sendLaterPresets,
} from '../lib/resurfaceTimes'

export interface ComposePageProps {
  /** Reply / reply_all / forward intent overrides used by
   *  ComposeDock. When `undefined`, falls back to the URL search
   *  params (page-mode behaviour). Pass `null` to force "no
   *  intent" regardless of the URL. */
  replyToIdOverride?: string | null
  replyAllIdOverride?: string | null
  forwardIdOverride?: string | null
  /** Dock-mode: the per-window draft id for a `new` compose. Each
   *  Compose click mints a fresh one so several independent new
   *  drafts can be open at once; resuming a saved draft reopens its
   *  exact slot. Ignored for reply/forward (those key off the source
   *  email id). Page-mode reads `?draft=` from the URL instead. */
  draftIdOverride?: string | null
  /** Optional pre-fill for `to:` and `subject:`. Mirrors the
   *  `?to=` + `?subject=` URL params. */
  initialToOverride?: string[]
  initialSubjectOverride?: string
  /** Optional HTML pre-fill for the body, seeded into the editor on
   *  first mount only (e.g. Kanban card → email). Never re-applied, so
   *  a resumed/typed draft is never clobbered. */
  initialBodyOverride?: string
  /** ARC-1: extra kanban card ids to link the SENT copy to (merged with
   *  any card chips embedded in the body). Used by the card-drawer
   *  inline composer so a reply/new message sent from a card shows
   *  under that card's related emails. */
  extraLinkedCardIds?: string[]
  /** Canonical job id supplied by an embedded host for a new compose.
   *  Reply/forward keep their source email's job link. */
  extraLinkedJobId?: number
  /** Called on successful send. Page-mode default = navigate to
   *  `/email/inbox` after 1.5s splash. Dock-mode passes
   *  `closeDock`. */
  onSent?: () => void
  /** Called on discard / close. Page-mode default = navigate to
   *  `/email/inbox`. */
  onClose?: () => void
  /** Called when a SEND attempt fails (server/network — not pre-send
   *  validation). Dock-mode uses this to restore a minimized window
   *  so the failure banner is actually seen; the draft is preserved
   *  either way. */
  onSendFailed?: () => void
  /** When true, suppress the page-only success splash — the dock
   *  closes immediately on send, so the splash would just flash
   *  and disappear. Also forces the narrow stacked field layout:
   *  the dock window is 380px wide regardless of the VIEWPORT, so
   *  the `sm:` media-query variants (viewport-based) must not
   *  apply inside it. */
  embed?: boolean
  /** Dock-mode: live subject for the window header. */
  onSubjectChange?: (subject: string) => void
  /** Dock-mode: live attachment count so Close can warn before
   *  dropping Files (they aren't persisted in the draft). */
  onAttachmentsCountChange?: (count: number) => void
}

export default function ComposePage(props: ComposePageProps = {}) {
  const {
    addFiles,
    aiDraft,
    aiDrafting,
    attachedOriginalIds,
    attachingOriginalId,
    attachments,
    attachmentsBusy,
    attachOriginalAttachment,
    bcc,
    bodyHtml,
    cc,
    closeSignatureModal,
    editorRef,
    emailAccounts,
    error,
    fileInputRef,
    fromAccountId,
    setFromAccountId,
    importance,
    setImportance,
    visibility,
    setVisibility,
    includeQuotechBranding,
    setIncludeQuotechBranding,
    generateAiDraft,
    handleDiscard,
    handleSaveSignature,
    handleSend,
    validateSendInputs,
    insertOriginalQuote,
    insertSignatureNow,
    intent,
    onSignatureError,
    onSignatureSaved,
    originalAttachments,
    signatureModalOpen,
    isSending,
    lastSavedLabel,
    quotedBlockPresent,
    refetchReplyEmail,
    replyEmail,
    replyEmailError,
    replyEmailLoading,
    sendFailed,
    setAttachments,
    setBcc,
    setBodyHtml,
    setCc,
    setShowAiDraft,
    setShowBcc,
    setShowCc,
    setSubject,
    setTo,
    showAiDraft,
    showBcc,
    showCc,
    showExpiryNotice,
    dismissExpiryNotice,
    subject,
    success,
    to,
  } = useComposePage(props)

  // Undo-send: clicking Send arms a configurable grace window during which the
  // operator can hit "Undo" before the email actually leaves. The window length
  // is the user's "Undo send" preference (Settings → Email; default 10s, "Off"
  // = 0 = send immediately) — read once at mount via lazy state so a settings
  // change applies on the next composer open. The hold fires the latest
  // handleSend (via a ref so any edits made during the window are included) and
  // flushes on unmount so a queued send is never silently dropped.
  const [undoDelayMs] = useState(() => getUndoSendDelayMs())
  // Source of truth is the DB; refresh the synchronous caches in the
  // background so a fresh device picks up the saved undo window and the
  // pinned/last-used From mailbox on the next compose open.
  useEffect(() => {
    void syncUndoSendFromServer()
    void syncFromDefaultsFromServer()
  }, [])
  // Pinned default From mailbox (FEAT-2026-07-03-COMPOSE-FROM-PIN). Local
  // mirror of the synchronous cache so the pin button repaints immediately;
  // the cache + server are written by togglePinFrom below.
  const [pinnedFromId, setPinnedFromId] = useState<number | null>(() => getPinnedFromAccountId())
  // Right-click context menu on the From select ("pin this address").
  const [fromMenuPos, setFromMenuPos] = useState<{ x: number; y: number } | null>(null)
  // Spreadsheet builder modal (FEAT-2026-07-03-COMPOSE-SPREADSHEET).
  const [workbookOpen, setWorkbookOpen] = useState(false)
  // Chart/flowchart builder modal (FEAT-2026-07-03-COMPOSE-CHARTS).
  const [chartOpen, setChartOpen] = useState(false)
  const { toast: fromPinToast } = useToast()
  const togglePinFrom = (accountId: number | null) => {
    if (accountId === null) return
    const next = pinnedFromId === accountId ? null : accountId
    setPinnedFromId(next)
    setFromMenuPos(null)
    fromPinToast(
      next === null
        ? 'Default From unpinned — the last-used address will be preselected.'
        : 'Pinned as your default From address.',
      'success',
    )
    void setPinnedFromAccount(next).then((ok) => {
      if (!ok) {
        fromPinToast(
          "Couldn't save the default to your account — it will apply on this device only.",
          'warning',
        )
      }
    })
  }
  const undoSend = useUndoSend(undoDelayMs)
  const handleSendRef = useRef(handleSend)
  handleSendRef.current = handleSend
  // Forgotten-attachment guard (FEAT-2026-07-06-COMPOSER-MICRO): the body
  // says "attached"/"enclosed" but nothing is attached → interrupt ONCE
  // with a confirm instead of letting the classic fuck-up leave. The flag
  // holds the pending intent so "Send anyway" resumes the exact same path.
  const [attachWarnOpen, setAttachWarnOpen] = useState(false)
  // Resurface train — Send-later / follow-up state. `followUpAt` rides
  // EVERY send (now or scheduled) as `followup_at`; the backend stamps the
  // sent copy `needs_reply` and the beat nudges on silence. The split
  // button's dropdown owns both pickers.
  const [sendMenuOpen, setSendMenuOpen] = useState(false)
  const [followUpAt, setFollowUpAt] = useState<string | null>(null)
  const followUpAtRef = useRef(followUpAt)
  followUpAtRef.current = followUpAt
  const sendOpts = () => ({ followupAt: followUpAtRef.current ?? undefined })
  const triggerSend = (skipAttachGuard = false) => {
    // Surface validation errors immediately rather than after the countdown.
    const validationError = validateSendInputs()
    if (validationError) {
      handleSend(sendOpts())
      return
    }
    if (!skipAttachGuard && shouldWarnMissingAttachment(subject, bodyHtml, attachments.length)) {
      setAttachWarnOpen(true)
      return
    }
    // "Off" (0) — skip the hold entirely and send now.
    if (undoDelayMs <= 0) {
      handleSend(sendOpts())
      return
    }
    undoSend.arm(() => handleSendRef.current(sendOpts()))
  }
  // Scheduled sends skip the undo hold — the message is already delayed
  // by construction and nothing leaves the mailbox for minutes/hours.
  const triggerScheduledSend = (fireAt: Date) => {
    setSendMenuOpen(false)
    void handleSend({
      scheduledAt: fireAt.toISOString(),
      followupAt: followUpAtRef.current ?? undefined,
    })
  }
  const sendBusy = isSending || attachmentsBusy || undoSend.pending

  // Cmd/Ctrl+Enter sends from anywhere in the composer (body, subject,
  // recipients) — the universal "send" shortcut operators expect from Gmail/
  // Outlook. We listen on the composer root so it works regardless of which
  // field has focus, and bail when a nested handler already consumed the
  // event (the AI-draft textarea uses the SAME chord to generate and calls
  // preventDefault, so e.defaultPrevented is set by the time it bubbles here).
  const onComposerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.defaultPrevented) return
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      if (!sendBusy) triggerSend()
    }
  }

  // Whole-page drop target — any file dropped anywhere on the composer
  // becomes an attachment (the body editor still embeds images inline
  // and lets the event bubble here for overlay cleanup). `addFiles`
  // enforces the 25MB/file + 35MB/message limits with a toast.
  const { isDragActive, dropzoneProps } = useComposeDragDrop(addFiles)

  // The dock window is 380px wide regardless of viewport width, so the
  // viewport-based `sm:` variants put the fields in cramped two-column
  // mode inside it. In embed mode always use the stacked narrow layout.
  const wide = !props.embed
  const fieldRowCls = wide
    ? 'flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2'
    : 'flex flex-col gap-1'
  const fieldLabelCls = wide
    ? 'text-xs uppercase tracking-wider sm:normal-case sm:tracking-normal sm:text-sm text-muted-foreground sm:w-16 sm:pt-2 shrink-0'
    : 'text-xs uppercase tracking-wider text-muted-foreground shrink-0'

  if (success && !props.embed) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-background">
        <CheckCircle className="h-12 w-12 text-emerald-500 mb-4" />
        <h2 className="text-lg font-semibold text-foreground">Email Sent!</h2>
        <p className="text-muted-foreground">Redirecting to inbox...</p>
      </div>
    )
  }

  return (
    <div className="relative flex flex-col h-full bg-background" {...dropzoneProps} onKeyDown={onComposerKeyDown}>
      {/* Drop-files overlay — visible while a file drag hovers anywhere
          over the composer. pointer-events-none so the drop itself
          reaches the handlers underneath. */}
      {isDragActive && (
        <div className="absolute inset-0 z-[60] pointer-events-none flex items-center justify-center bg-primary/5 border-2 border-dashed border-primary rounded-lg m-2">
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border border-border shadow-md">
            <UploadCloud className="h-5 w-5 text-primary" />
            <span className="text-sm font-medium text-foreground">
              Drop files to attach
            </span>
          </div>
        </div>
      )}
      {/* Header.
          BUG-0037 mobile-parity (Mark Newman 2026-05-17 "It can be a
          lot better"): on a 360px viewport the header was carrying
          X + title + "Replying to" + Signature button + paperclip
          + "Send" button-with-text. Total horizontal demand was
          ~360px before padding, so the right cluster spilled or
          crushed the title. Compactions:
            - p-4 → p-3 on narrow (saves 8px each edge).
            - "Last saved …" label drops to icon-tooltip on narrow.
            - "Signature" → icon-only at <sm (the icon is the same
              pencil-on-paper metaphor; text reappears desktop+up).
            - Send button text becomes a paper-plane icon on narrow;
              "Send"/"Sending..." label reappears from sm+.
          Total saved: ~120px horizontal at 360px width. */}
      <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          {/* type="button" on EVERY composer button: ComposePage is
              embedded in host shells (master dashboard tabs, CRM
              drawers, the dock) that may wrap content in a <form>  - 
              an untyped button defaults to type=submit there and the
              click triggers a form submit instead of (only) the
              handler. The footer Attach button always declared it;
              the header ones didn't ("attachment button on the bottom
              works but not at the top", 2026-06-10). */}
          <button
            type="button"
            onClick={handleDiscard}
            className="p-2 rounded-lg hover:bg-accent shrink-0"
            aria-label="Discard draft"
            title="Discard draft"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
          {/* Editable title = the email subject (single source of truth).
              Typing here updates `subject`, which drives the recipient's
              inbox subject, the "Subject:" field below, AND the dock/tab
              label via the onSubjectChange effect - so the window title is
              no longer a dead "New Message" string. When empty it falls
              back to an intent-aware placeholder so the context (Reply /
              Forward / New) is never lost. */}
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={intent === 'reply' ? 'Reply'
              : intent === 'reply_all' ? 'Reply All'
                : intent === 'forward' ? 'Forward'
                  : 'New Message'}
            aria-label="Subject"
            title="Subject - shown on the tab and in the recipient's inbox"
            className="min-w-0 flex-1 bg-transparent border-0 p-0 text-base sm:text-lg font-semibold text-foreground placeholder:text-muted-foreground focus:outline-none truncate"
          />
          {lastSavedLabel && wide && (
            <span
              className="hidden sm:inline text-xs text-muted-foreground truncate"
              title={lastSavedLabel}
            >
              {lastSavedLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {/* Explicit, LABELLED Discard — the bare top-left "X" reads as
              "close", so operators couldn't find how to delete a draft
              (operator 2026-06-07: "where is the delete version in this
              draft"). A red trash button + word makes the destructive
              action obvious. Same handler as the X (confirm-then-clear +
              remove the Outlook draft copy). */}
          <button
            type="button"
            onClick={handleDiscard}
            className="text-xs px-2 py-1.5 rounded text-destructive hover:bg-destructive/10 inline-flex items-center gap-1.5"
            title="Delete this draft"
            aria-label="Delete draft"
          >
            <Trash2 className="h-4 w-4" />
            <span className={wide ? 'hidden sm:inline' : 'hidden'}>Discard</span>
          </button>
          <button
            type="button"
            onClick={handleSaveSignature}
            className={`text-xs px-2 py-1.5 rounded text-muted-foreground hover:bg-accent items-center gap-1.5 ${wide ? 'hidden sm:inline-flex' : 'hidden'}`}
            title="Edit your email signature"
            aria-label="Edit signature"
          >
            Signature
          </button>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(Array.from(e.target.files))
              e.target.value = ''
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-lg hover:bg-accent relative"
            aria-label={attachments.length > 0 ? `${attachments.length} attachment${attachments.length === 1 ? '' : 's'}` : 'Attach files'}
            title="Attach files"
          >
            <Paperclip className="h-5 w-5 text-muted-foreground" />
            {attachments.length > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-primary text-primary-foreground text-[10px] rounded-full flex items-center justify-center">
                {attachments.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => triggerSend()}
            disabled={sendBusy}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
            aria-label={
              undoSend.pending
                ? `Sending in ${undoSend.secondsLeft} seconds`
                : isSending
                  ? 'Sending email'
                  : attachmentsBusy
                    ? 'Attaching files'
                    : 'Send email'
            }
            title={
              undoSend.pending
                ? `Sending in ${undoSend.secondsLeft}s; use Undo to cancel`
                : isSending
                  ? 'Sending…'
                  : attachmentsBusy
                    ? 'Attaching files…'
                    : 'Send  (⌘/Ctrl + Enter)'
            }
          >
            {sendBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            <span className={wide ? 'hidden sm:inline' : 'hidden'}>
              {undoSend.pending
                ? `Sending in ${undoSend.secondsLeft}s`
                : isSending
                  ? 'Sending...'
                  : attachmentsBusy
                    ? 'Attaching…'
                    : 'Send'}
            </span>
          </button>
          {/* Send options (resurface train): Send later presets +
              "remind me if no reply". Split-button chevron so the primary
              Send stays one-click. The amber dot marks an armed reminder. */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setSendMenuOpen((v) => !v)}
              disabled={sendBusy}
              className="relative flex items-center px-1.5 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
              aria-label="Send options"
              aria-expanded={sendMenuOpen}
              title={
                followUpAt
                  ? `Send options — reminder armed for ${formatResurfaceTime(followUpAt)}`
                  : 'Send options (Send later, follow-up reminder)'
              }
            >
              <ChevronDown className="h-4 w-4" />
              {followUpAt && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" />
              )}
            </button>
            {sendMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-[70]"
                  onClick={() => setSendMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-[71] w-64 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg py-1">
                  <div className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Clock className="h-3 w-3" /> Send later
                  </div>
                  {sendLaterPresets().map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center justify-between gap-2"
                      onClick={() => triggerScheduledSend(p.at)}
                    >
                      <span>{p.label}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {formatResurfaceTime(p.at)}
                      </span>
                    </button>
                  ))}
                  <div className="my-1 border-t border-border" />
                  <div className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <BellRing className="h-3 w-3" /> Remind me if no reply
                  </div>
                  {followupPresets().map((p) => {
                    const iso = p.at.toISOString()
                    const active = followUpAt === iso
                    return (
                      <button
                        key={p.key}
                        type="button"
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center justify-between gap-2 ${active ? 'text-primary font-medium' : ''}`}
                        onClick={() => {
                          setFollowUpAt(active ? null : iso)
                          setSendMenuOpen(false)
                        }}
                      >
                        <span>{p.label}</span>
                        {active && <CheckCircle className="h-3.5 w-3.5" />}
                      </button>
                    )
                  })}
                  {followUpAt && (
                    <button
                      type="button"
                      className="w-full text-left px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent"
                      onClick={() => {
                        setFollowUpAt(null)
                        setSendMenuOpen(false)
                      }}
                    >
                      Clear reminder
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Undo-send window. While armed, the email has NOT left yet — the
          operator gets a grace period to catch a wrong recipient or a
          forgotten attachment. "Send now" skips the wait; "Undo" cancels
          with the draft untouched. */}
      {undoSend.pending && (
        <SendHoldBanner
          secondsLeft={undoSend.secondsLeft}
          onSendNow={undoSend.sendNow}
          onUndo={undoSend.cancel}
        />
      )}

      {/* Forgotten-attachment confirm: the body claims an attachment but
          none is attached. One interruption, two exits — attach or send. */}
      {attachWarnOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-label="No attachment warning"
        >
          <div className="w-full max-w-sm rounded-xl border border-border bg-background p-5 shadow-xl">
            <div className="text-sm font-semibold mb-1">Send without an attachment?</div>
            <p className="text-sm text-muted-foreground mb-4">
              Your message mentions an attachment, but nothing is attached yet.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                autoFocus
                onClick={() => {
                  setAttachWarnOpen(false)
                  fileInputRef.current?.click()
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-border hover:bg-accent transition-colors"
              >
                Add attachment
              </button>
              <button
                type="button"
                onClick={() => {
                  setAttachWarnOpen(false)
                  triggerSend(true)
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Send anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* One-time heads-up that drafts are kept on the operator's
          account and auto-purge after 14 days. Surfaces the first
          time a draft is saved; "Don't show again" persists the
          dismissal (tenant-scoped localStorage flag). */}
      {showExpiryNotice && (
        <div className="mx-4 mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-lg flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-sm text-blue-800 dark:text-blue-200">
            <p>
              This draft is saved to your account and will sync across your
              devices. Unsent drafts are <strong>automatically deleted after
              14 days</strong>.
            </p>
            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={() => dismissExpiryNotice(false)}
                className="text-xs font-medium px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Got it
              </button>
              <button
                type="button"
                onClick={() => dismissExpiryNotice(true)}
                className="text-xs text-blue-700 dark:text-blue-300 hover:underline"
              >
                Don&apos;t show again
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => dismissExpiryNotice(false)}
            className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-800/40 shrink-0"
            aria-label="Dismiss notice"
          >
            <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </div>
      )}

      {error && (
        <div className="mx-4 mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <span className="text-sm text-destructive flex-1 min-w-0">
            {sendFailed ? `Send failed - your draft is safe. ${error}` : error}
          </span>
          {sendFailed && (
            <button
              type="button"
              onClick={() => handleSend(sendOpts())}
              disabled={isSending || attachmentsBusy}
              className="shrink-0 px-3 py-1.5 text-sm font-medium rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 transition-colors"
              aria-label="Retry sending email"
            >
              {isSending ? 'Retrying…' : 'Retry send'}
            </button>
          )}
        </div>
      )}

      {/* Body container.
          BUG-0037 mobile-parity: `p-4` (16px on every edge) consumed
          32px of horizontal width - push to `p-3 sm:p-4` so the
          recipient inputs get back ~16px on a 360px screen. */}
      <div className="flex-1 flex flex-col p-3 sm:p-4 overflow-y-auto">
        <div className="space-y-3 mb-4">
          {/* Recipient rows.
              BUG-0037 mobile-parity: the previous layout used a 64px
              left-column label (`w-16`) + input + Cc/Bcc buttons on
              the right of the To row. On a 360px viewport after
              padding that left ~232px for the autocomplete input,
              which cramps the dropdown and breaks at common email
              addresses. Stack the label above the input on narrow
              widths so the input takes the full row width; keep the
              two-column horizontal layout from sm+ (desktop density
              is unchanged). Cc/Bcc affordances move below the To
              input on narrow so they're not competing for space.
              The label keeps its w-16 from sm+ so vertical alignment
              across all four rows (To/Cc/Bcc/Subject) is preserved
              on desktop. */}
          <div className={fieldRowCls}>
            <label className={fieldLabelCls}>To:</label>
            <div className="flex-1 min-w-0">
              <RecipientAutocomplete
                value={to}
                onChange={setTo}
                placeholder="Start typing a name or email…"
                ariaLabel="To recipients"
                autoFocus={(intent === 'new' || intent === 'forward') && to.length === 0}
              />
              {(!showCc || !showBcc) && (
                <div className={`flex gap-3 mt-1 ${wide ? 'sm:hidden' : ''}`}>
                  {!showCc && (
                    <button type="button" onClick={() => setShowCc(true)} className="text-xs text-primary hover:underline">Add Cc</button>
                  )}
                  {!showBcc && (
                    <button type="button" onClick={() => setShowBcc(true)} className="text-xs text-primary hover:underline">Add Bcc</button>
                  )}
                </div>
              )}
            </div>
            {wide && (
              <div className="hidden sm:flex gap-1 pt-2">
                {!showCc && (
                  <button type="button" onClick={() => setShowCc(true)} className="text-sm text-primary hover:underline">Cc</button>
                )}
                {!showBcc && (
                  <button type="button" onClick={() => setShowBcc(true)} className="text-sm text-primary hover:underline">Bcc</button>
                )}
              </div>
            )}
          </div>

          {showCc && (
            <div className={fieldRowCls}>
              <label className={fieldLabelCls}>Cc:</label>
              <RecipientAutocomplete
                value={cc} onChange={setCc}
                placeholder="cc@email.com" ariaLabel="Cc recipients"
              />
            </div>
          )}

          {showBcc && (
            <div className={fieldRowCls}>
              <label className={fieldLabelCls}>Bcc:</label>
              <RecipientAutocomplete
                value={bcc} onChange={setBcc}
                placeholder="bcc@email.com" ariaLabel="Bcc recipients"
              />
            </div>
          )}

          <div className={wide ? 'flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2' : 'flex flex-col gap-1'}>
            <label className={wide ? 'text-xs uppercase tracking-wider sm:normal-case sm:tracking-normal sm:text-sm text-muted-foreground sm:w-16 shrink-0' : 'text-xs uppercase tracking-wider text-muted-foreground shrink-0'}>Subject:</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="flex-1 px-3 py-2 border-b border-border bg-transparent focus:outline-none focus:border-primary text-foreground"
            />
          </div>

          {/* Send-as ("From") mailbox + outbound priority. The From
              selector only appears when the operator can actually send
              from more than one connected mailbox (a single-account user
              has nothing to choose). Importance is always available. */}
          {(() => {
            const sendable = emailAccounts.filter((a) => typeof a.id === 'number')
            const showFrom = sendable.length > 1
            const currentFrom = sendable.find((account) => account.id === fromAccountId) ?? sendable[0]
            const fromAddress = currentFrom?.email_address || ''
            const copyFromAddress = () => {
              if (!fromAddress) return
              void navigator.clipboard.writeText(fromAddress).catch(() => undefined)
            }
            return (
              <div className={wide ? 'flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2' : 'flex flex-col gap-1'}>
                <label className={wide ? 'text-xs uppercase tracking-wider sm:normal-case sm:tracking-normal sm:text-sm text-muted-foreground sm:w-16 shrink-0' : 'text-xs uppercase tracking-wider text-muted-foreground shrink-0'}>From:</label>
                <div className="flex flex-1 min-w-0 flex-wrap items-center gap-2">
                  {showFrom ? (
                    <>
                      <select
                        value={fromAccountId ?? ''}
                        onChange={(e) => setFromAccountId(e.target.value ? Number(e.target.value) : null)}
                        onContextMenu={(e) => {
                          // Right-click → pin menu (FEAT-2026-07-03-COMPOSE-FROM-PIN)
                          e.preventDefault()
                          setFromMenuPos({ x: e.clientX, y: e.clientY })
                        }}
                        aria-label="Send from mailbox"
                        className="flex-1 min-w-0 px-2 py-1.5 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:border-primary"
                      >
                        {sendable.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.display_name ? `${a.display_name} <${a.email_address}>` : a.email_address}
                            {pinnedFromId === a.id ? ' 📌' : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => togglePinFrom(fromAccountId)}
                        aria-label={
                          pinnedFromId !== null && pinnedFromId === fromAccountId
                            ? 'Unpin default From address'
                            : 'Pin as default From address'
                        }
                        title={
                          pinnedFromId !== null && pinnedFromId === fromAccountId
                            ? 'Pinned as your default From — click to unpin'
                            : 'Pin this address as your default From'
                        }
                        className={`shrink-0 p-1.5 rounded border transition-colors ${
                          pinnedFromId !== null && pinnedFromId === fromAccountId
                            ? 'border-primary text-primary bg-primary/10'
                            : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
                        }`}
                      >
                        <Pin className="h-3.5 w-3.5" />
                      </button>
                      {fromMenuPos && (
                        <>
                          {/* click-away backdrop for the pin context menu */}
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setFromMenuPos(null)}
                            onContextMenu={(e) => { e.preventDefault(); setFromMenuPos(null) }}
                          />
                          <div
                            role="menu"
                            className="fixed z-50 min-w-[220px] rounded-md border border-border bg-popover text-popover-foreground shadow-md py-1"
                            style={{
                              // Clamp inside the viewport: near the right/bottom
                              // edge (dock-mode composer, small screens) the raw
                              // cursor position pushed the menu off-screen.
                              left: Math.max(8, Math.min(fromMenuPos.x, window.innerWidth - 236)),
                              top: Math.max(8, Math.min(fromMenuPos.y, window.innerHeight - 160)),
                            }}
                          >
                            <div className="px-3 py-1.5 text-xs text-muted-foreground select-text break-all">
                              {fromAddress || 'Default mailbox'}
                            </div>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                copyFromAddress()
                                setFromMenuPos(null)
                              }}
                              className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent text-left"
                            >
                              <Copy className="h-3.5 w-3.5" />
                              Copy address
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => togglePinFrom(fromAccountId)}
                              className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent text-left"
                            >
                              <Pin className="h-3.5 w-3.5" />
                              {pinnedFromId !== null && pinnedFromId === fromAccountId
                                ? 'Unpin default From'
                                : 'Pin as default From'}
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <span
                      className="text-sm text-muted-foreground truncate flex-1 min-w-0 select-text"
                      data-quotech-address={sendable[0]?.email_address || undefined}
                      onContextMenu={(event) => {
                        const address = sendable[0]?.email_address
                        if (!address) return
                        event.preventDefault()
                        void navigator.clipboard.writeText(address).catch(() => undefined)
                      }}
                    >
                      {sendable[0]?.email_address || 'Default mailbox'}
                    </span>
                  )}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-xs text-muted-foreground">Priority:</span>
                    <select
                      value={importance}
                      onChange={(e) => setImportance(e.target.value as 'low' | 'normal' | 'high')}
                      aria-label="Message priority"
                      className="px-2 py-1.5 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:border-primary"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  {/* Change #1210. Who can open the saved copy of this send. */}
                  <EmailVisibilityToggle
                    value={visibility}
                    onChange={(next) => setVisibility(next)}
                    size="md"
                  />
                </div>
              </div>
            )
          })()}

          {(replyEmail?.job_id ?? props.extraLinkedJobId) != null && (
            <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Briefcase className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-blue-700 dark:text-blue-300">
                Linked to Job #{replyEmail?.job_id ?? props.extraLinkedJobId}
              </span>
            </div>
          )}

          {/* Reply / forward intent status banner — closes the
              2026-05-17 "I clicked reply first, the email I am
              replying to did not come on" gap. The prefill effect
              fires asynchronously after `useEmail()` resolves, so on
              a slow network the operator could land in compose with
              no visible quote and assume the feature is broken.
              This banner surfaces the loading state explicitly, AND
              gives a manual "Insert original" fallback if the prefill
              was skipped (resumed draft with empty body, fetch error,
              etc.) so the operator is never stuck with an empty
              reply. The banner self-hides once a quote is present in
              the body - `quotedBlockPresent` is a literal substring
              check on the marker the prefill effect inserts. */}
          {intent !== 'new' && (
            <div className="p-2 rounded-lg border border-border bg-muted/40 flex items-center gap-2 text-xs">
              {replyEmailLoading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  <span className="text-muted-foreground">Loading original email…</span>
                </>
              ) : replyEmailError ? (
                <>
                  <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                  <span className="text-destructive">Couldn't load original email.</span>
                  <button
                    type="button"
                    onClick={() => refetchReplyEmail()}
                    className="ml-auto px-2 py-0.5 text-[11px] rounded border border-border hover:bg-accent"
                  >
                    Retry
                  </button>
                </>
              ) : replyEmail ? (
                <>
                  <span className="text-muted-foreground truncate">
                    {intent === 'forward' ? 'Forwarding' : 'Replying to'}: <span className="text-foreground">{replyEmail.from_name || replyEmail.from_address || '(unknown sender)'}</span>
                    {replyEmail.subject ? ` - ${replyEmail.subject}` : ''}
                  </span>
                  {!quotedBlockPresent && (
                    <button
                      type="button"
                      onClick={insertOriginalQuote}
                      className="ml-auto shrink-0 px-2 py-0.5 text-[11px] rounded border border-border hover:bg-accent"
                      title="Insert the original email as a quoted block at the bottom of the body"
                    >
                      Insert original
                    </button>
                  )}
                </>
              ) : null}
            </div>
          )}
        </div>

        {/* 2026-05-13: attachments moved OUT of this scrollable
            region - they used to render between the recipients/
            subject form and the body editor, which the operator
            (rightly) called "annoying". Gmail-style: now pinned to
            a slim strip directly above the editor footer (see the
            block after </RichTextEditor> below) so they never
            interrupt the writing flow. */}

        {/* Actions lane — the Draft-with-AI button, any original files
            still pending attach, AND the attached-file cards all live on
            ONE row directly under the recipients (operator 2026-06-07:
            "why is the attachment showing there, not in the line with
            Draft with AI"). The attachment cards render inline so they
            sit beside the button instead of on a separate strip below.
            Shown whenever there's an AI action (reply/forward) or any
            attachment (covers new-message compose too). */}
        {(replyEmail || attachments.length > 0 || intent === 'new') && (
          <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-border pb-3">
            {/* From-scratch "Draft with AI" — only for a brand-new message
                (reply/forward use the reply-shaped panel below). The brief →
                subject+body call rides the same /ai/email/draft-reply endpoint
                with mode=compose. */}
            {intent === 'new' && (
              <AiComposeButton
                onResult={({ subject: aiSubject, body }) => {
                  if (aiSubject && !subject.trim()) setSubject(aiSubject)
                  if (body) editorRef.current?.insertHtml(aiBodyToHtml(body))
                }}
              />
            )}
            {replyEmail && (!showAiDraft ? (
              <button type="button"
                onClick={async () => {
                  setShowAiDraft(true)
                  await generateAiDraft({
                    subject: replyEmail.subject || '',
                    body_text: replyEmail.body_text || replyEmail.body_preview || '',
                    from_name: replyEmail.from_name || replyEmail.from_address,
                  })
                }}
                disabled={aiDrafting}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-gradient-to-r from-violet-500/10 to-purple-500/10 text-violet-700 dark:text-violet-300 rounded-lg hover:from-violet-500/20 hover:to-purple-500/20 transition-all border border-violet-200/50 dark:border-violet-800/50"
              >
                {aiDrafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {aiDrafting ? 'Drafting with AI...' : 'Draft with AI'}
              </button>
            ) : aiDraft ? (
              <div className="w-full p-3 bg-gradient-to-r from-violet-50/80 to-purple-50/50 dark:from-violet-950/20 dark:to-purple-950/10 rounded-lg border border-violet-200/50 dark:border-violet-800/40">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3" /> AI Suggested Reply
                  </span>
                  <div className="flex items-center gap-1">
                    <button type="button"
                      onClick={() => {
                        const html = `<p>${aiDraft.body.replace(/\n/g, '<br>')}</p>`
                        editorRef.current?.insertHtml(html)
                        setShowAiDraft(false)
                      }}
                      className="px-2.5 py-1 text-[10px] font-medium bg-violet-600 text-white rounded hover:bg-violet-700 transition-colors"
                    >
                      Insert at caret
                    </button>
                    <button type="button" onClick={() => setShowAiDraft(false)} className="p-1 rounded hover:bg-accent">
                      <X className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed">{aiDraft.body}</p>
              </div>
            ) : aiDrafting ? (
              <div className="w-full p-3 bg-violet-50/50 dark:bg-violet-950/20 rounded-lg border border-violet-200/30 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-violet-500" />
                <span className="text-sm text-muted-foreground">Generating draft with AI...</span>
              </div>
            ) : null)}

            {/* Original-message files still pending attach — these only
                appear if auto-attach failed to download one (it then
                stays here as a manual retry). Reply/forward only. */}
            {intent !== 'new' && originalAttachments
              .filter((att) => !attachedOriginalIds.has(att.id))
              .map((att) => {
                const busy = attachingOriginalId === att.id
                const signed = isSignedAttachmentName(att.filename)
                return (
                  <button
                    key={att.id}
                    type="button"
                    onClick={() => attachOriginalAttachment(att)}
                    disabled={busy}
                    className="group flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm border bg-muted border-border hover:border-primary/50 hover:bg-accent transition-colors"
                    title={`Attach ${att.filename} to your reply`}
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                    ) : signed ? (
                      <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    ) : (
                      <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="truncate max-w-[150px]">{att.filename}</span>
                    <span className="text-[10px] font-medium shrink-0 text-primary">Attach</span>
                  </button>
                )
              })}

            {/* Attached-file cards, inline in the SAME lane — Outlook-style
                (type icon + Signed badge + click-to-preview). */}
            <ComposeAttachmentStrip
              inline
              files={attachments}
              onRemove={(i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
            />
          </div>
        )}

        <RichTextEditor
          ref={editorRef}
          value={bodyHtml}
          onChange={setBodyHtml}
          onFile={addFiles}
          onMention={(person) => {
            // @mention in the body adds the person to the To list
            // (operator decision 2026-06-18). Dedupe case-insensitively.
            const addr = person.email.trim()
            if (!addr) return
            setTo((prev) =>
              prev.some((e) => e.toLowerCase() === addr.toLowerCase())
                ? prev
                : [...prev, addr],
            )
          }}
          placeholder={
            intent === 'forward'
              ? 'Add a note here…'
              : intent === 'new'
                ? 'Write your message…  (paste or drop images directly into the body - they\'ll be embedded inline)'
                : 'Type your reply here…'
          }
          ariaLabel="Email body"
          className="flex-1"
          onAiTransform={(action, text) => transformSelection(action, text)}
          onAiInstruct={(instruction, selectionHtml) => runAiInstruction({ instruction, selectionHtml })}
        />

        {/* "Powered by Quotech" marketing footer chip
            (FEAT-2026-07-07-GTM-FOOTER-OPT-IN). Renders ONLY when the
            footer will actually ship, directly under the body/signature
            area — what-you-see-is-what-ships. Remove flips it off for
            this send without touching the saved default; the default
            itself is opt-in via Settings → Email. */}
        {includeQuotechBranding && (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-teal-200/70 dark:border-teal-800/50 bg-teal-50/60 dark:bg-teal-950/20 px-2.5 py-1.5 text-xs">
            <img
              src="https://dashboard.quotech.ai/mascot/beaver-foreman-v2.gif"
              alt=""
              aria-hidden="true"
              className="h-5 w-[18px] shrink-0"
            />
            <span className="min-w-0 truncate text-teal-800 dark:text-teal-200">
              The <span className="font-semibold">“Powered by Quotech”</span> marketing
              footer will be added below your signature.
            </span>
            <button
              type="button"
              onClick={() => setIncludeQuotechBranding(false)}
              className="ml-auto shrink-0 flex items-center gap-1 px-2 py-0.5 rounded border border-teal-300/70 dark:border-teal-700/60 text-teal-700 dark:text-teal-300 hover:bg-teal-100/70 dark:hover:bg-teal-900/40 transition-colors"
              title="Send this email without the Quotech marketing footer. Change the default in Settings → Email."
              aria-label="Remove the Quotech marketing footer from this email"
            >
              <X className="h-3 w-3" />
              Remove
            </button>
          </div>
        )}

        {/* Compose footer — second, more discoverable Attach button
            beneath the editor. The header paperclip stays for power-
            user muscle memory, but the operator screenshot showed
            the inline button was easy to overlook tucked next to
            Send. The button lives INSIDE the scroll region so it
            stays adjacent to the editor regardless of viewport
            height (no fixed-positioning surprises on smaller
            laptops). */}
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-accent text-foreground/80 hover:text-foreground transition-colors"
            title="Attach files"
          >
            <Paperclip className="h-3.5 w-3.5" />
            <span>Attach files</span>
            {attachments.length > 0 && (
              <span className="text-[10px] text-muted-foreground tabular-nums">
                ({attachments.length})
              </span>
            )}
          </button>
          {/* Spreadsheet builder (FEAT-2026-07-03-COMPOSE-SPREADSHEET):
              shared platform grid → attach as .xlsx or insert as table. */}
          <button
            type="button"
            onClick={() => setWorkbookOpen(true)}
            className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-accent text-foreground/80 hover:text-foreground transition-colors"
            title="Build a spreadsheet (attach as .xlsx or insert as a table)"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            <span>Spreadsheet</span>
          </button>
          {/* Chart/flowchart builder (FEAT-2026-07-03-COMPOSE-CHARTS):
              recharts + React Flow → attach as .png or insert inline. */}
          <button
            type="button"
            onClick={() => setChartOpen(true)}
            className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-accent text-foreground/80 hover:text-foreground transition-colors"
            title="Build a chart or flowchart (attach as .png or insert inline)"
          >
            <BarChart3 className="h-3.5 w-3.5" />
            <span>Chart</span>
          </button>
          <div className="flex-1" />
          {lastSavedLabel && (
            <span className="text-[11px] text-muted-foreground/80">{lastSavedLabel}</span>
          )}
        </div>
      </div>

      {workbookOpen && (
        <ComposeWorkbookModal
          defaultName={subject}
          onAttach={(file) => addFiles([file])}
          onInsertHtml={(html) => editorRef.current?.insertHtml(html)}
          notify={(message, kind) => fromPinToast(message, kind)}
          onClose={() => setWorkbookOpen(false)}
        />
      )}

      {chartOpen && (
        <ComposeChartModal
          defaultName={subject}
          onAttach={(file) => addFiles([file])}
          onInsertHtml={(html) => editorRef.current?.insertHtml(html)}
          onClose={() => setChartOpen(false)}
          notify={(message, kind) => fromPinToast(message, kind)}
        />
      )}

      <SignatureEditorModal
        open={signatureModalOpen}
        onClose={closeSignatureModal}
        onSaved={onSignatureSaved}
        onInsertNow={insertSignatureNow}
        onError={onSignatureError}
        accounts={emailAccounts}
      />
    </div>
  )
}

/**
 * Page-mode route wrapper that REMOUNTS ComposePage whenever the URL
 * addresses a different draft/intent. Standalone `/compose` routes (master
 * shell, per-app routers) render THIS instead of <ComposePage/> directly so
 * that clicking Compose while a draft is open opens a genuinely blank
 * message rather than re-slotting the previous draft's body. React keeps the
 * same element mounted across a search-param-only navigation, so without a
 * changing `key` the old state (recipients, subject, pasted images) survived
 * into the "new" compose. The remount's unmount flushes the previous draft
 * to Drafts first, so nothing is lost. Mirrors the inline key InboxPage uses
 * where ComposePage is embedded in its right pane.
 */
export function ComposePageRoute(props: ComposePageProps = {}) {
  const { search } = useLocation()
  const composeKey = useMemo(() => {
    const p = new URLSearchParams(search)
    const draft = p.get('draft')
    if (draft) return `draft:${draft}`
    const reply = p.get('reply')
    if (reply) return `reply:${reply}`
    const replyAll = p.get('reply_all')
    if (replyAll) return `reply_all:${replyAll}`
    const forward = p.get('forward')
    if (forward) return `forward:${forward}`
    return 'new'
  }, [search])
  // Split surface (operator 2026-06-23): compose on the left, a collapsible
  // recent-inbox rail on the right so the inbox stays "active" while writing.
  //
  // In-place preview (operator 2026-06-29: "view emails from the compose rail
  // without losing draft changes"): clicking a rail row swaps the rail for a
  // reader pane in the SAME right column while ComposePage stays mounted on
  // the left — its autosave keeps running, so the draft is never lost. "Back
  // to inbox" (reader's own back arrow) clears the preview and restores the
  // rail. Compose is keyed by URL only, so opening a preview never remounts /
  // resets the draft. Hidden < lg inside each child (compose owns the surface
  // on narrow viewports).
  const [previewId, setPreviewId] = useComposePreviewEmail()
  const allocationMembers = useEmailAllocationMembers()
  return (
    <div className="flex h-full min-h-0 w-full">
      <div className="min-w-0 flex-1">
        <ComposePage key={composeKey} {...props} />
      </div>
      {previewId ? (
        <aside className="hidden lg:flex w-[600px] max-w-[55%] flex-col border-l border-border bg-background">
          <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground">
            <CheckCircle className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
            <span>Your draft is still open on the left and autosaving - nothing is lost.</span>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden">
            <EmailReader
              emailId={previewId}
              onBack={() => setPreviewId(null)}
              backLabel="Back to inbox"
              onArchived={() => setPreviewId(null)}
              onTrashed={() => setPreviewId(null)}
              onSwitchEmail={(id) => setPreviewId(id)}
              showAISidebar={false}
              allocationMembers={allocationMembers ?? undefined}
            />
          </div>
        </aside>
      ) : (
        <ComposeInboxRail onOpenEmail={setPreviewId} activeEmailId={previewId} />
      )}
    </div>
  )
}
